import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Euro, Save, AlertTriangle, Package, Info } from 'lucide-react';
import BackButton from '@/components/BackButton';

// Restbestand-Optionen
const REST_OPTIONS = [
  { value: 0, label: '0', description: 'Automat war komplett leer' },
  { value: 25, label: '1/4', description: '25% Restbestand' },
  { value: 50, label: '1/2', description: '50% Restbestand' },
  { value: 75, label: '3/4', description: '75% Restbestand' },
  { value: 100, label: 'voll', description: '100% - Automat war noch voll' },
];

const AutomatAuszaehlenPage = () => {
  const { qr, id } = useParams();
  const navigate = useNavigate();
  const { API } = useContext(AuthContext);
  const [automat, setAutomat] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [restFraction, setRestFraction] = useState(0); // Default: ganz leer
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadTimeout, setLoadTimeout] = useState(false);
  const [error, setError] = useState(null);

  // Dev logging - sichtbar ohne Konsole
  const isDev = process.env.NODE_ENV === 'development';

  // Berechne Rückbuchungsmenge
  // current_fill kann Slot-basiert sein: {"1": {"quantity": 100, ...}} oder einfach: {"quantity": 100}
  const getCapacityFromCurrentFill = (currentFill) => {
    if (!currentFill || typeof currentFill !== 'object') return 0;
    
    // Prüfe ob direktes quantity-Feld existiert
    if ('quantity' in currentFill) {
      return currentFill.quantity || 0;
    }
    
    // Slot-basierte Struktur - summiere alle Slots
    let total = 0;
    for (const key in currentFill) {
      if (currentFill[key] && typeof currentFill[key] === 'object' && 'quantity' in currentFill[key]) {
        total += currentFill[key].quantity || 0;
      }
    }
    return total;
  };
  
  const capacityUnits = getCapacityFromCurrentFill(automat?.current_fill);
  const restUnitsCalculated = Math.floor(capacityUnits * restFraction / 100);

  useEffect(() => {
    // Defensive: Check if params exist
    if (!qr && !id) {
      setError('Keine Automat-ID angegeben');
      setLoading(false);
      return;
    }

    if (isDev) {
      console.log('🔍 Navigating to counting for:', { qr, id });
    }

    fetchAutomat();

    // Timeout fallback: 10 Sekunden
    const timer = setTimeout(() => {
      if (loading) {
        setLoadTimeout(true);
        setLoading(false);
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [qr, id]);

  const fetchAutomat = async () => {
    try {
      if (isDev) {
        console.log('📡 Fetching automat data...');
      }

      let response;
      if (id) {
        response = await axios.get(`${API}/automaten/${id}`);
      } else if (qr) {
        response = await axios.get(`${API}/automaten/qr/${qr}`);
      } else {
        throw new Error('Keine ID oder QR-Code vorhanden');
      }

      if (isDev) {
        console.log('✅ Automat data received:', response?.data);
      }

      // Defensive: Check if response and data exist
      if (!response || !response.data) {
        throw new Error('Keine Daten vom Server erhalten');
      }
      
      const validStatuses = ['AUSZÄHLEN', 'Lager VOLL', 'AKTIV'];
      const invalidStatuses = ['Lager LEER'];
      
      // Hinweis: Defekte Automaten dürfen ausgezählt werden (nur Hinweis, keine Blockierung)
      // is_defect wird im automat-State gespeichert und als Banner angezeigt
      
      if (!validStatuses.includes(response.data.status)) {
        if (invalidStatuses.includes(response.data.status)) {
          toast.error(`Dieser Automat (Status: ${response.data.status}) kann nicht ausgezählt werden. Der Automat ist bereits leer.`);
        } else {
          toast.error(`Dieser Automat (Status: ${response.data.status}) kann nicht ausgezählt werden`);
        }
        setError(`Ungültiger Status: ${response.data.status}`);
        setLoading(false);
        setTimeout(() => navigate('/automaten', { replace: true }), 2000);
        return;
      }
      
      setAutomat(response.data);
      setError(null);
    } catch (error) {
      console.error('❌ Fetch automat error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Automat nicht gefunden';
      toast.error(errorMsg);
      setError(errorMsg);
      setLoading(false);
      setTimeout(() => navigate('/automaten', { replace: true }), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const amountValue = parseFloat(amount);
    if (isNaN(amountValue) || amountValue < 0) {
      toast.error('Bitte geben Sie einen gültigen Betrag ein (0 oder höher)');
      return;
    }

    // Validierung: Bei Restbestand > 0 muss Befüllmenge definiert sein
    if (restFraction > 0 && capacityUnits <= 0) {
      toast.error('Befüllmenge des Automaten ist nicht definiert. Bitte Automatenkonfiguration prüfen.');
      return;
    }

    setSaving(true);
    try {
      // Send amount and rest_fraction in request body
      const response = await axios.post(`${API}/automaten/${automat.id}/auszaehlen`, {
        amount: amountValue,
        note: note || null,
        rest_fraction: restFraction
      });
      
      let successMsg = 'Automat erfolgreich ausgezählt!';
      if (response.data?.lager_buchung_erstellt) {
        successMsg += ` (${response.data.rest_units_calculated} Stück ins Lager zurückgebucht)`;
      }
      
      toast.success(successMsg);
      navigate('/automaten');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Auszählen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        {isDev && (qr || id) && (
          <div className="mb-4 px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded">
            🔍 Loading automat: {qr || id}
          </div>
        )}
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <p className="mt-4 text-slate-600">Lade Automat-Daten...</p>
      </div>
    );
  }

  // Timeout fallback
  if (loadTimeout && !automat) {
    return (
      <Card className="max-w-2xl mx-auto mt-8 p-8">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-700 mb-4">⏱️ Zeitüberschreitung</h2>
          <p className="text-slate-600 mb-4">
            Die Daten konnten nicht innerhalb von 10 Sekunden geladen werden.
          </p>
          <BackButton label="Zurück zur Übersicht" className="mt-4" />
        </div>
      </Card>
    );
  }

  // Error state
  if (error && !automat) {
    return (
      <Card className="max-w-2xl mx-auto mt-8 p-8">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-700 mb-4">❌ Fehler</h2>
          <p className="text-slate-600 mb-4">
            Daten konnten nicht geladen werden: {error}
          </p>
          <div className="flex justify-center space-x-3 mt-6">
            <BackButton label="Zurück zur Übersicht" />
            <Button onClick={() => window.location.reload()}>
              Neu laden
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // No automat data
  if (!automat) {
    return (
      <Card className="max-w-2xl mx-auto mt-8 p-8">
        <div className="text-center">
          <h2 className="text-xl font-bold text-slate-700 mb-4">🤷 Keine Daten</h2>
          <p className="text-slate-600 mb-4">
            Automat-Daten sind nicht verfügbar. Sie werden weitergeleitet...
          </p>
          <BackButton label="Zurück zur Übersicht" className="mt-4" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in pb-24 sm:pb-6">
      {/* Dev logging - visible without console */}
      {isDev && (qr || id) && (
        <div className="px-3 py-2 bg-blue-100 text-blue-800 text-sm rounded">
          🔍 Navigating to counting for automatId: {qr || id}
        </div>
      )}

      <div className="flex items-center space-x-4">
        <BackButton />
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
            Automat auszählen
          </h1>
          <p className="text-slate-600 text-sm sm:text-base">{automat?.code || 'Unbekannt'}</p>
        </div>
      </div>

      <Card className="p-6 bg-yellow-50 border-2 border-yellow-300">
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-200 p-3 rounded-xl">
              <Euro className="text-yellow-700" size={32} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-yellow-900">Auszählung erforderlich</h2>
              <p className="text-yellow-700">
                Dieser Automat wurde aus einem Gehäuse getauscht und muss jetzt ausgezählt werden.
              </p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-yellow-200">
            {/* Hinweis-Banner für defekte Automaten */}
            {automat.is_defect && (
              <div className="mb-4 p-3 bg-orange-100 border border-orange-300 rounded-lg flex items-start gap-3">
                <AlertTriangle className="text-orange-600 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-orange-800">Hinweis: Dieser Automat ist als defekt markiert</p>
                  <p className="text-sm text-orange-700">
                    Das Auszählen ist dennoch möglich. Der Defektstatus bleibt nach dem Auszählen unverändert.
                  </p>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-3 text-sm mb-4">
              <div><span className="font-medium">Automat:</span> {automat.code}</div>
              <div><span className="font-medium">Typ:</span> {automat.machine_type}</div>
              <div><span className="font-medium">Münztyp:</span> {automat.coin_type} €</div>
              <div>
                <span className="font-medium">Status:</span>
                <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                  {automat.status}
                </span>
                {automat.is_defect && (
                  <span className="ml-2 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    DEFEKT
                  </span>
                )}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="amount" className="text-base font-semibold">
                  Geldbetrag eingeben (€)
                </Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  required
                  className="text-xl h-14 text-center font-bold"
                  autoFocus
                  data-testid="auszaehlen-amount-input"
                />
                <p className="text-sm text-slate-500 mt-2">
                  Der Betrag kann auch 0 sein, wenn der Automat leer war.
                </p>
              </div>

              {/* Restbestand-Auswahl */}
              <div className="border-t border-slate-200 pt-4">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Package size={18} />
                  Restbestand im Automaten
                </Label>
                <p className="text-sm text-slate-500 mb-3">
                  Wie viel Ware war noch im Automaten?
                </p>
                
                <div className="flex justify-center gap-2 sm:gap-3" data-testid="restbestand-options">
                  {REST_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setRestFraction(option.value)}
                      className={`flex flex-col items-center justify-center min-w-[60px] sm:min-w-[70px] p-2 sm:p-3 rounded-lg border-2 transition-all text-center ${
                        restFraction === option.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 hover:border-slate-300 text-slate-600'
                      }`}
                      data-testid={`restbestand-option-${option.value}`}
                    >
                      <span className="block font-semibold text-base sm:text-lg">{option.label}</span>
                      <span className="block text-xs opacity-75">{option.value}%</span>
                    </button>
                  ))}
                </div>

                {/* Anzeige der berechneten Rückbuchung */}
                {restFraction > 0 && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        {capacityUnits > 0 ? (
                          <>
                            <p className="font-medium text-blue-800">
                              Rückbuchung ins Lager: <span className="text-lg">{restUnitsCalculated} Stück</span>
                            </p>
                            <p className="text-blue-600">
                              Basis: Befüllmenge {capacityUnits} Stück × {restFraction}%
                            </p>
                          </>
                        ) : (
                          <p className="text-orange-700 font-medium">
                            ⚠️ Befüllmenge nicht definiert – Rückbuchung nicht möglich
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="note">Notiz (optional)</Label>
                <Textarea
                  id="note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Zusätzliche Bemerkungen..."
                  rows={2}
                  data-testid="auszaehlen-note-input"
                />
              </div>

              {/* Desktop Buttons */}
              <div className="hidden sm:flex gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 h-12 text-lg bg-green-600 hover:bg-green-700"
                  data-testid="auszaehlen-submit-btn"
                >
                  <Save size={20} className="mr-2" />
                  {saving ? 'Wird gespeichert...' : 'Speichern und abschließen'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="h-12"
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Card>

      {/* Mobile Sticky Action Button - mit Safe Area */}
      <div className="mobile-action-bar sm:hidden">
        <Button
          type="button"
          disabled={saving || !amount}
          onClick={handleSubmit}
          className="w-full h-12 text-lg bg-green-600 hover:bg-green-700"
          data-testid="auszaehlen-submit-btn-mobile"
        >
          <Save size={20} className="mr-2" />
          {saving ? 'Wird gespeichert...' : 'Speichern'}
        </Button>
      </div>
    </div>
  );
};

export default AutomatAuszaehlenPage;
